import { type Progress } from '@/james-playground/aa-family-claim/claim/engine';
import { type Handoff } from '@/james-playground/aa-family-claim/claim/engine';

/** Copy for the hub, rounds, Before we start and the handoffs. */

export const HUB = {
  title: 'Attendance Allowance',
  headline: {
    start: 'Let’s get started',
    good: 'Good start',
    most: 'Most of it is done',
    ready: 'Ready to send',
    stronger: 'Ready to send. A few things would make it stronger',
  } satisfies Record<Progress, string>,
  intro: {
    start: 'We’ll build the claim together, a bit at a time. Start with a voice note.',
    good: 'Every answer is saved as you go, so you can stop and come back.',
    most: 'A few more and it’s ready for your Care Adviser.',
    ready: 'Everything the form needs is answered or set aside to talk through.',
    stronger: 'Everything the form needs is answered. These would make the claim stronger.',
  } satisfies Record<Progress, string>,
  areasLabel: 'The claim',
  quickest: 'Quickest next step',
  more: 'Or',
  toGo: (n: number) => `${n} to go`,
  state: { not_started: 'Not started', in_progress: 'In progress', done: 'Done' },
  since: 'Since you were last here',
  sinceLine: (n: number, date: string) =>
    `${n} ${n === 1 ? 'answer' : 'answers'} from your Care Adviser call on ${date}, to check before sending.`,
  added: (n: number, area: string) =>
    `Added ${n} ${n === 1 ? 'answer' : 'answers'} to ${area}`,
  addedNone: 'Saved. Nothing new to add from that.',
  sendBy: (by: string, from: string) =>
    `Send by ${by} to keep ${from} as the start date.`,
  sendByClose: 'If it’s close, send what you have. Your Care Adviser can follow up on the rest.',
  qualifying: (date: string) =>
    `Payments could start from ${date}, once she has needed help for 6 months. You can still send the claim now.`,
  check: 'Check and send',
  change: 'change',
  tasks: 'When you can',
  tasksStartDate: 'Protect the start date',
  tasksSr1: 'Ask her doctor or nurse for an SR1',
  stronger: 'Make it stronger',
  skipStronger: 'Send it as it is',
  quicker: 'On the quicker route',
  step: {
    opening: 'Tell me about a typical day',
    voice: 'Tell me in a voice note',
    batch: 'A quick check of the rest',
    document: 'Add a photo',
  },
  stepDetail: {
    opening: 'One voice note answers most of the form.',
    batch: (n: number) => `${n} everyday things you haven’t mentioned yet`,
    covers: (area: string) => `Fills in most of ${area}`,
  },
};

export const BEFORE = {
  title: 'Before we start',
  intro: 'A few things that decide which form fits, and how it’s signed.',
  have: 'Here’s what we have',
  next: 'Continue',
  routeChanged: 'That changes which form fits. Let’s look at that first.',
};

export const ROUND = {
  have: 'What we have so far',
  sayInstead: 'Say it instead',
  typeInstead: 'Type it instead',
  photo: 'Add a photo',
  upload: 'Upload a file',
  noDocument: 'I don’t have one',
  reading: 'Reading it now',
  found: {
    voice: 'From your voice note',
    typed: 'From what you wrote',
    prescription: 'From the prescription',
    hospital_letter: 'From the letter',
    care_plan: 'From the care plan',
  },
  clash: 'This doesn’t match what we had. You’ll choose at the check.',
  clashTitle: 'We have two answers for this',
  remove: 'Not right',
  removed: 'Removed',
  done: 'Back to the claim',
  added: 'Added to the claim. You’ll check everything before sending.',
  nothing: 'I couldn’t find anything for the form in that. It’s kept for your Care Adviser.',
  keepGoing: 'Next one',
  later: 'Finish later',
  orbLabel: 'Tap to talk',
  listening: 'Listening…',
  stop: 'Stop recording',
  send: 'Send recording',
  delete: 'Delete recording',
  typePlaceholder: 'Or type it here',
  sendTyped: 'Send',
  consentTitle: 'Before you tell me',
  consentBody:
    'KareHero uses AI to turn what you say, type or photograph into answers for the form. You check every answer before it goes on the form, and your Care Adviser sees the claim. Nothing goes to DWP until the form is signed.',
  consentAccept: 'I’m happy with that',
  consentDecline: 'Ask me the questions instead',
  openingQuicker: {
    she: 'Tell me about {name}’s health, and who helps her.',
    you: 'Tell me about your health, and who helps you.',
  },
};

export type HandoffCopy = {
  title: string;
  body: string;
  action: { label: string; href: string };
  phone?: string;
};

export const HANDOFF: Record<Handoff, HandoffCopy> = {
  scotland: {
    title: 'In Scotland, she claims Pension Age Disability Payment',
    body: 'Social Security Scotland pays Pension Age Disability Payment in place of Attendance Allowance. You can start it online or by phone, and her answers here are kept for it.',
    action: { label: 'Start on mygov.scot', href: 'https://www.mygov.scot/pension-age-disability-payment' },
    phone: '0800 182 2222',
  },
  northernIreland: {
    title: 'Northern Ireland has its own claim form',
    body: 'Attendance Allowance in Northern Ireland is claimed through nidirect, online or with the NI form. Her answers here are kept.',
    action: { label: 'Claim on nidirect', href: 'https://www.nidirect.gov.uk/services/claim-attendance-allowance' },
  },
  abroad: {
    title: 'Living outside Great Britain changes the claim',
    body: 'Attendance Allowance depends on living in Great Britain. Your Care Adviser can check what she can claim from where she lives.',
    action: { label: 'Read the rules on GOV.UK', href: 'https://www.gov.uk/attendance-allowance/eligibility' },
  },
  underSpa: {
    title: 'Before State Pension age, she claims PIP',
    body: 'Attendance Allowance is for people over State Pension age. Until then, Personal Independence Payment covers the same help, at the same rates.',
    action: { label: 'Claim PIP on GOV.UK', href: 'https://www.gov.uk/pip' },
  },
  hasAa: {
    title: 'She already gets Attendance Allowance',
    body: 'If her needs have changed, or her award is ending, that’s a change or a renewal, made with DWP. A new claim isn’t needed.',
    action: { label: 'Report a change on GOV.UK', href: 'https://www.gov.uk/attendance-allowance/report-change-in-circumstances' },
    phone: '0800 731 0122',
  },
  hasPip: {
    title: 'She keeps PIP, DLA or Adult Disability Payment',
    body: 'These can’t be paid alongside Attendance Allowance, and she keeps them after State Pension age. If her needs have changed, tell the benefit that pays her.',
    action: { label: 'Read about PIP changes', href: 'https://www.gov.uk/pip/change-of-circumstances' },
  },
  pipEnded: {
    title: 'Her PIP ended in the last 12 months',
    body: 'Someone whose PIP or Adult Disability Payment ended in the last year claims PIP again, even after State Pension age.',
    action: { label: 'Claim PIP on GOV.UK', href: 'https://www.gov.uk/pip' },
  },
  bereavement: {
    title: 'We’re sorry for your loss',
    body: 'A claim can still be finished after someone dies, by the person dealing with their affairs. Your Care Adviser will talk you through it.',
    action: { label: 'Read about claims after a death', href: 'https://www.gov.uk/after-a-death' },
  },
};

export const HANDOFF_COMMON = {
  adviser: 'Talk to your Care Adviser',
  kept: 'Everything you’ve told us is kept.',
  back: 'Go back and change an answer',
};

export const CHECK = {
  title: 'Check the answers',
  description: 'These came from your voice note, photos and earlier calls. Confirm each one before it goes on the form.',
  toCheck: 'To check',
  everythingElse: 'Everything else on the form',
  confirmAll: 'They’re all right',
  settleFirst: (n: number) =>
    n === 1
      ? 'Pick one of the two answers that disagree to carry on.'
      : `Pick one answer in each of the ${n} that disagree to carry on.`,
  next: 'Continue',
  clash: 'These two don’t agree',
  onForm: (boxes: string) => `On the form: ${boxes}`,
};

export const SEND = {
  title: 'Send to your Care Adviser',
  description: 'They check it with you, go through anything you set aside, then you print and sign it.',
  talkThrough: 'To talk through with your Care Adviser',
  nothingSkipped: 'Nothing set aside.',
  pdf: 'Download the AA1 to look at',
  send: 'Send to my Care Adviser',
  howItGoes: 'How it reaches DWP',
  route: 'Print it, sign it where it’s marked, and post it in the envelope your Care Adviser sends. They’ll tell you what to enclose.',
};

export const WELCOME = {
  title: {
    she: 'Let’s get {name}’s Attendance Allowance claim ready',
    you: 'Let’s get your Attendance Allowance claim ready',
  },
  description: {
    she: 'You’ve already told us a lot about {name}. We’ll start with that and ask only for the rest.',
    you: 'You’ve already told us a lot. We’ll start with that and ask only for the rest.',
  },
  worth: 'Attendance Allowance is £76.70 or £114.60 a week, depending on the help needed. It isn’t means-tested.',
  howLabel: 'How it works',
  how: [
    { title: 'Tell us about her day', body: 'Talk, type or add a photo. We pick out what the form needs.' },
    { title: 'We show what’s still needed', body: 'Each time, the quickest way to answer the rest comes first.' },
    { title: 'Check it and send it', body: 'Your Care Adviser goes through anything you set aside, then you sign.' },
  ],
  primary: 'Start',
};

/** The Care Adviser's side of the same claim (spec 12). */
export const ADVISER = {
  inbox: 'Claims to review',
  received: (from: string, relationship: string) =>
    `From ${from}, her ${relationship.toLowerCase()} · today`,
  receivedSelf: 'From the claimant · today',
  title: (name: string) => `${name} · Attendance Allowance`,
  signedIn: 'Signed in · sample adviser account',
  tiles: {
    answered: 'answers checked by the family',
    talkThrough: (n: number) => (n === 1 ? 'question to talk through' : 'questions to talk through'),
    deadline: 'send by, to keep the start date',
  },
  flags: {
    quicker: 'Quicker route',
    inCare: (place: string) => (place ? `In a ${place.toLowerCase()}` : 'In care'),
    funded: 'Council or NHS funded',
    attorney: 'Attorney signs',
    applying: 'Applying to be appointed',
    deputy: 'Deputy signs',
    appointee: 'Appointee signs',
    organisation: 'Organisation signs',
    unsure: 'Signer to confirm',
    herself: 'Claiming herself',
  },
  source: (source: string, from: string) =>
    ({
      profile: 'Care profile',
      account: `${from}’s KareHero account`,
      voice: `${from}’s voice note`,
      typed: `Typed by ${from}`,
      prescription: 'Prescription photo',
      hospital_letter: 'Hospital or clinic letter',
      care_plan: 'Care plan',
      call_notes: 'Your earlier call',
      batch_check: `Confirmed by ${from} in the batch check`,
      family: `Answered by ${from}`,
      adviser: 'You',
    })[source] ?? 'Answered',
  talkThroughLabel: 'To talk through',
  talkThroughNone: 'Nothing set aside.',
  answersLabel: 'The claim',
  download: 'Download the AA1 draft',
  call: (name: string) => `Call ${name}`,
  reviewed: 'Mark as reviewed',
};
